<template>
  <div fluid id="service-new">
    <HeaderBar>
      <v-icon slot="icon">add_box</v-icon>
      Add New Staffing Request
    </HeaderBar>
    <v-layout row wrap>
      <v-flex grow class="mr-2">
        <v-switch v-model="bulkCreate" label="Bulk request" />
      </v-flex>
      <v-flex grow>
        <v-tooltip bottom>
          <span
            slot="activator"
            class="v-label theme--light"
            style="cursor: help; display: inline-block; margin-left: -80px; margin-top: 17px"
          >
            <v-icon>help_outline</v-icon>
            What position to post?
          </span>
          <span style="padding: 16px; display: block; max-width: 600px;">
            <h2>TA - Technician Assistant</h2>
            <ul>
              <li>Can room patients, take histories</li>
              <li>Can restrain patients</li>
              <li>
                Can give pre-dosed oral medications with appropriate
                technician/DVM approval
              </li>
              <li>
                Can clean, stock, feed, care for patients and other hospital
                needs
              </li>
              <li>Can clean, sterilize, and wrap packs and gowns</li>
              <li>
                CANNOT administer IV/IM medications, draw blood or place IV
                catheters
              </li>
              <li>
                CANNOT run radiographs (can assist with them as long as they
                have a dosimeter badge from VSS)
              </li>
              <li>
                CANNOT induce/monitor anesthesia (Includes sedated procedures)
              </li>
              <li>CANNOT perform dentals</li>
            </ul>

            <h2>
              Advanced Technician Assistant (Click box on TA 'Require Advanced')
            </h2>
            <ul>
              <li>Advanced Assistant can do the same as a TA but also:</li>
              <li>
                Can administer IV/IM medications under direct supervision and
                dose calculated by CrVT or DVM
              </li>
              <li>Can draw blood</li>
              <li>Can place IV Catheters</li>
              <li>Can run radiographs with dosimeter badge from VSS</li>
              <li>
                CANNOT induce/monitor anesthesia (Includes sedated procedures)
              </li>
              <li>CANNOT perform dentals</li>
            </ul>

            <h2>CVT - Credentialed Veterinary Technician (+New Grad)</h2>
            <ul>
              <li>
                CVT can do the same as an Advanced Technician Assistant but
                also:
              </li>
              <li>Induce/Monitor Anesthesia (includes sedated procedures)</li>
              <li>Calculate doses of medications</li>
              <li>Administer IV/IM Medications</li>
              <li>Perform Dentals</li>
            </ul>
          </span>
        </v-tooltip>
      </v-flex>
    </v-layout>

    <div v-if="bulkCreate">
      <BulkShiftCreator />
    </div>

    <div v-else>
      <v-form v-model="valid" ref="form">
        <v-layout row wrap>
          <v-flex grow>
            <v-layout column>
              <!-- <v-layout row>
                {{ mappedEligibleEmployees }}
              </v-layout> -->
              <v-layout row>
                <v-flex grow class="mr-2">
                  <v-autocomplete
                    v-model="location"
                    :rules="locationRules"
                    :items="possibleLocations"
                    item-text="name"
                    item-value="id"
                    label="Location"
                    clearable
                    required
                    class="required"
                    return-object
                    autocomplete="off"
                  >
                  </v-autocomplete>
                </v-flex>
                <v-flex grow>
                  <v-autocomplete
                    style="display: inline-block"
                    v-model="position"
                    :rules="positionRules"
                    :items="possiblePositions"
                    item-text="name"
                    item-value="id"
                    label="Position"
                    required
                    class="required"
                    return-object
                    autocomplete="off"
                  >
                  </v-autocomplete>
                  <v-checkbox
                    v-if="position && position.name == 'CVT'"
                    style="display: inline-block"
                    label="Allow New Grads"
                    v-model="allowNewGrads"
                  >
                  </v-checkbox>
                </v-flex>
                <v-flex v-if="position && position.name == 'Assistant'">
                  <v-checkbox
                    label="Require Advanced"
                    v-model="requireAdvancedAssistants"
                  >
                  </v-checkbox>
                  <div
                    v-if="requireAdvancedAssistants"
                    style="width: 200px; display: inline-block"
                  >
                    <small>
                      (This employee is not a technician, but can place
                      catheters, draw blood and administer pre-dosed
                      medications. They cannot run anesthesia or perform
                      dentals.)
                    </small>
                  </div>
                </v-flex>
              </v-layout>

              <ShiftScheduleSelector
                :start="shiftStart"
                :end="shiftEnd"
                @updateStart="updateShiftStart"
                @updateEnd="updateShiftEnd"
              />

              <v-checkbox
                label="Advanced Options"
                v-model="showAdvanced"
              ></v-checkbox>

              <div v-if="showAdvanced" class="mb-4">
                <v-layout row>
                  <v-flex>
                    <v-checkbox
                      label="Repeating request"
                      :disabled="coordinatedCoverage && user.role == 'client'"
                      v-model="repeats"
                      :rules="repeatRules"
                      persistent-hint
                      :hint="
                        coordinatedCoverage && user.role == 'client'
                          ? 'Coordinated Coverage shifts cannot repeat'
                          : ''
                      "
                    >
                    </v-checkbox>
                  </v-flex>
                  <v-flex v-if="repeats" shrink>
                    <v-select
                      label="Repetition"
                      :items="repetitionOptions"
                      item-text="label"
                      item-value="key"
                      v-model="repeatFrequency"
                    >
                    </v-select>
                  </v-flex>
                  <v-flex v-if="repeats">
                    <v-menu
                      ref="repeatDurationPicker"
                      v-model="repeatDurationPicker"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      :return-value.sync="repeatDuration"
                      lazy
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <template slot="activator" slot-scope="{ on }">
                        <v-text-field
                          v-model="repeatDuration"
                          label="Repeat Until"
                          prepend-icon="event"
                          readonly
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="repeatDuration"
                        no-title
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          flat
                          color="primary"
                          @click="
                            $refs.repeatDurationPicker.save(repeatDuration)
                          "
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-menu>
                  </v-flex>
                </v-layout>
                <v-layout row v-if="canCreateCoordinatedCoverageRequests">
                  <v-flex shrink class="mr-2">
                    <v-checkbox
                      label="Coordinated Coverage"
                      v-model="coordinatedCoverage"
                      :rules="[coordinatedRules]"
                    >
                    </v-checkbox>
                    <div
                      style="max-width: 400px"
                      v-if="user.role == 'client'"
                      class="subtle"
                    >
                      Please note: when using the Coordinated Coverage option,
                      the Employee must first accept this request before the
                      shift is created.
                    </div>
                  </v-flex>
                  <v-flex grow>
                    <v-autocomplete
                      label="Employee"
                      v-show="coordinatedCoverage && location && position"
                      :items="mappedEligibleEmployees"
                      item-text="name"
                      item-value="id"
                      @click="eligibleEmployeeSearch"
                      v-model="assignedEmployee"
                      clearable
                      loading="true"
                      return-object
                    >
                    </v-autocomplete>
                  </v-flex>
                  <v-flex shrink>
                    <v-checkbox
                      style="max-width: 300px"
                      label="Must be this employee?"
                      v-show="assignedEmployee"
                      v-model="mustFulfill"
                      persistent-hint
                      hint="If selected, the request will be cancelled if the employee rejects it. Otherwise, the shift will become available for other eligible employees upon rejection."
                    >
                    </v-checkbox>
                  </v-flex>
                </v-layout>
                <v-divider class="my-4"></v-divider>
                <v-layout row>
                  <v-flex>
                    <v-text-field
                      class="straight-bonus-field"
                      label="Bonus?"
                      prefix="$"
                      v-model="straightBonusAmount"
                      hint="the Assigned Employee receives 2/3 of the total bonus offered."
                    >
                    </v-text-field>
                  </v-flex>
                  <v-layout row>
                    <v-flex v-if="user.managerOrHigher" class="ml-2">
                      <v-text-field
                        label="Call In Bonus?"
                        :hint="hasCoverBonus ? 'Not billed to client' : ''"
                        persistent-hint
                        prefix="$"
                        v-model="callInBonusAmount"
                      >
                      </v-text-field>
                    </v-flex>
                    <v-flex v-if="user.managerOrHigher">
                      <v-switch
                        label="Cover Bonus"
                        v-model="hasCoverBonus"
                      ></v-switch>
                    </v-flex>
                  </v-layout>
                  <v-flex v-if="user.managerOrHigher" class="ml-2">
                    <v-text-field
                      label="Travel Bonus?"
                      prefix="$"
                      v-model="travelBonusAmount"
                    >
                    </v-text-field>
                  </v-flex>
                </v-layout>

                <v-layout column>
                  <v-checkbox label="Limit to skills" v-model="limitedToSkills">
                  </v-checkbox>
                  <v-layout
                    v-if="limitedToSkills || skillsSelected.length > 0"
                    row
                    wrap
                    jusitify-space-around
                  >
                    <v-flex xs4 v-for="skill in possibleSkills" :key="skill.id">
                      <v-checkbox
                        :label="skill.name"
                        :value="skill.id"
                        v-model="skillsSelected"
                      ></v-checkbox>
                    </v-flex>
                    <v-flex>
                      <v-tooltip bottom>
                        <v-btn
                          class="ml-0"
                          outline
                          color="success"
                          slot="activator"
                          @click="saveSkills"
                          :disabled="!this.location"
                          :pending="skillsPending"
                          :loading="skillsPending"
                        >
                          Save these as default
                        </v-btn>
                        <span>
                          Save this set of selected skills as the default for
                          new service requests at this location
                        </span>
                      </v-tooltip>
                    </v-flex>
                  </v-layout>
                </v-layout>
                <v-divider class="my-4"></v-divider>
                <v-layout row>
                  <v-flex>
                    <v-textarea box label="Details" v-model="details">
                    </v-textarea>
                  </v-flex>
                </v-layout>
                <v-layout row>
                  <v-flex shrink>
                    <v-checkbox
                      label="Sunset"
                      hint="Cancel this shift if not filled by time chosen"
                      persistent-hint
                      v-model="sunsetOn"
                      :rules="sunsetRules"
                    ></v-checkbox>
                  </v-flex>
                  <v-flex v-show="canSetSunsetDays">
                    <v-select
                      label="Cancel Shift"
                      :items="sunsetAfterDayRange"
                      v-model="sunsetAfterDays"
                      style="display: inline-block"
                    ></v-select>

                    <span v-if="sunsetOn && sunsetAfterDays">
                      days from start of shift
                    </span>
                  </v-flex>
                </v-layout>
              </div>
            </v-layout>
          </v-flex>

          <v-flex shrink>
            <v-layout column>
              <v-flex>
                <v-btn
                  @click="maybeCreateShift()"
                  block
                  :disabled="!valid || !shiftStart || !shiftEnd"
                  color="success"
                >
                  Save &amp; Create
                </v-btn>
              </v-flex>
              <v-flex>
                <v-btn block @click="clearForm" color="error">
                  Clear Form
                </v-btn>
              </v-flex>
              <v-flex>
                <!-- testing -->
                <!-- <v-btn block @click="eligibleEmployeeSearch" color="warn">
                  Test Employee Search
                </v-btn>
                <v-btn block @click="clearEligibleEmployees" color="warn">
                  Clear employees
                </v-btn> -->
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-form>
    </div>
    <v-dialog v-model="autoCallinWarning" maxWidth="350">
      <v-card>
        <v-card-title>
          <h1>Short-notice Warning</h1>
        </v-card-title>
        <v-card-text>
          {{ autoCallinMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            flat
            @click="
              autoCallinWarning = false;
              autoCallinMessage = '';
            "
            >Go Back</v-btn
          >
          <v-btn color="success" small @click="createShift"> Accept </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import BulkShiftCreator from "@/components/service/bulk-shift-creator.vue";
import HeaderBar from "@/components/base/header-bar.vue";
import ShiftScheduleSelector from "@/components/service/shift-schedule-selector.vue";
import { range } from "lodash";

import shiftApi from "@/api/shift";

export default {
  components: {
    BulkShiftCreator,
    HeaderBar,
    ShiftScheduleSelector
  },
  created() {
    this.$store.dispatch("serviceRequest/getOptions", this.user.id);
    this.$store.dispatch("serviceRequest/getSkills").then(res => {
      this.possibleSkills = res;
    });
    // This has been removed as it causes issues.
    // if( this.$route.query.startDate) {
    //   this.shiftStart = this.$route.query.startDate
    // }
  },
  watch: {
    location: function(newVal) {
      this.skillsSelected = [];

      // NOTE:
      // This endpoint fails due to a partially removed feature.
      // The relationship between worksites and default skills was removed.
      // However, adding that relationship back causes unexpected behavior.

      // if (newVal) {
      //     var that = this;
      //     this.$store.dispatch("serviceRequest/getRequestPreferences", this.location.id).then((res) => {
      //         if (res !== null) {
      //             const prefs = res.rules.find((p) => p.type === "skills");
      //             if (prefs && prefs.defaults.length > 0) {
      //                 that.skillsSelected = prefs.defaults.map((s) => s.id);
      //                 this.limitedToSkills = true;
      //                 this.showAdvanced = true;
      //             }
      //         }
      //     });
      // } else {
      //     this.skillsSelected = [];
      // }
    },
    coordinatedCoverage: function(newVal, oldVal) {
      if (!newVal) {
        this.employeeUserId = null;
      } else {
        this.repeats = false;
        this.repeatFrequency = null;
        this.repeatDuration = null;
      }
    },
    repeats: function(newVal, oldVal) {
      if (!newVal) {
        this.repeatFrequency = null;
        this.repeatDuration = null;
      }
    },
    limitedToSkills: function(newVal, oldVal) {
      if (!newVal) {
        this.skillsSelected = [];
      }
    },
    sunsetOn(val) {
      if (!val) {
        this.sunsetAfterDays = null;
        this.sunsetDate = null;
      } else {
        this.sunsetAfterDays = 3;
      }
    }
    // skillsSelected: function(newVal) {
    //   if (!newVal.empty) {
    //     this.limitedToSkills = true
    //     this.showAdvanced = true
    //   }
    // },
  },
  computed: {
    ...mapState("userAuth", ["user"]),
    ...mapState("serviceRequest", [
      "possibleLocations",
      "possiblePositions",
      "assignableEmployees",
      "newRequestPreferences"
    ]),
    mappedEligibleEmployees() {
      return this.eligibleEmployees.map(e => {
        return { name: e.display_name, id: e.id };
      });
    },
    canCreateCoordinatedCoverageRequests() {
      if (
        this.user &&
        (this.user.managerOrHigher ||
          this.user.privileges.find(
            p => p == "canCreateCoordinatedCoverageRequests"
          ))
      ) {
        return true;
      }
      return false;
    },
    sunsetRules() {
      if (this.sunsetOn) {
        let shiftStartDate = new Date(this.shiftStart);
        let sunsetDate = new Date(shiftStartDate);
        sunsetDate.setDate(shiftStartDate.getDate() - this.sunsetAfterDays);
        this.sunsetDate = sunsetDate;

        let today = new Date();
        let daysBetween = 0;
        while (today < shiftStartDate) {
          daysBetween++;
          today.setDate(today.getDate() + 1);
        }
        if (daysBetween < 7) {
          this.canSetSunsetDays = false;
          return [
            "In order to sunset, shift start date must be at least 7 days out from shift request date."
          ];
        }
        let daysBetweenTodayAndShiftStart = daysBetween;

        today = new Date();
        if (sunsetDate < today) {
          this.canSetSunsetDays = false;
          return ["Sunset date must be in the future."];
        }

        today = new Date();
        daysBetween = 0;
        while (today < sunsetDate) {
          daysBetween++;
          today.setDate(today.getDate() + 1);
        }
        if (daysBetween < 3) {
          this.canSetSunsetDays = false;
          return [
            "In order to sunset, sunset date must be at least 72 hours out from shift request date."
          ];
        }

        // 2 is a magic number that represents number of days (> 72 hour) buffer from request date needed
        this.sunsetAfterDayRange = range(3, daysBetweenTodayAndShiftStart - 2);
        this.canSetSunsetDays = true;
      }
      // no errors
      return [];
    },
    repeatRules() {
      const rules = [];
      if (this.repeats && !this.repeatFrequency) {
        rules.push("Repetition frequency is required");
      }
      if (this.repeats && this.repeatFrequency && !this.repeatDuration) {
        rules.push("Repetition duration is required");
      }
      if (
        this.repeatDuration &&
        this.$moment(this.repeatDuration).isBefore(this.$moment(this.shiftEnd))
      ) {
        rules.push("Repetition cannot end before the first shift");
      }
      return rules;
    },
    coordinatedRules() {
      var coordinatedCoverageCheck = this.location && this.position;
      if (!coordinatedCoverageCheck) {
        var coordinatedCoverageRules = "Must select a location and position";
      }
      return coordinatedCoverageRules;
    }
  },
  methods: {
    updateShiftStart(val) {
      this.shiftStart = val;
      this.setRepetitionOptions(val);
    },
    updateShiftEnd(val) {
      this.shiftEnd = val;
    },
    saveSkills() {
      this.skillsPending = true;

      const skillSet = this.possibleSkills.filter(s => {
        return this.skillsSelected.includes(s.id);
      });
      const worksiteID = this.location ? this.location.id : null;

      shiftApi.saveNewRequestPreferences(
        { worksiteID, skillSet },
        success => {
          this.skillsPending = false;
        },
        fail => {
          alert("that did not work: ", fail);
          this.skillsPending = false;
        }
      );

      setTimeout(() => {
        this.skillsPending = false;
      }, 1000);
    },

    // Testing method:
    eligibleEmployeeSearch() {
      let locationId = this.location ? this.location.id : null;
      let positionId = this.position ? this.position.id : null;
      let newGradAllowed = this.allowNewGrads;
      let advancedAssistantRequired = this.requireAdvancedAssistants;
      let params = {
        location_id: locationId,
        position_id: positionId,
        allow_new_grad: newGradAllowed,
        allow_advanced_assistant: advancedAssistantRequired
      };
      this.$store.dispatch("employees/search", params).then(response => {
        this.eligibleEmployees = response.filter(e => {
          // Don't include deactivated employees
          return !e.deactivated;
        });
      });
    },
    // Testing method:
    clearEligibleEmployees() {
      this.eligibleEmployees = [];
    },
    clearForm() {
      this.$refs.form.reset();
    },
    setRepetitionOptions: function(value) {
      if (value) {
        const dayOfWeek = this.$moment(this.shiftStart).format("dddd");
        const isAWeekday =
          this.$moment(this.shiftStart).isoWeekday() < 6 ? true : false;
        var options = [];
        options.push({ label: "Daily", key: "daily", order: 1 });
        if (isAWeekday) {
          options.push({
            label: "All Weekdays",
            key: "weekdays",
            order: 2
          });
        }
        if (!isAWeekday) {
          options.push({
            label: "On all Weekend days",
            key: "weekends",
            order: 2
          });
        }
        options.push({
          label: `Weekly on ${dayOfWeek}s`,
          key: "weekly",
          order: 3
        });

        this.repetitionOptions = options.slice().sort((a, b) => {
          a.order - b.order;
        });
      }
    },

    maybeCreateShift() {
      var assignedUserId = null;
      if (this.assignedEmployee) {
        var assignedUserId = this.assignedEmployee.id;
      }
      shiftApi
        .checkAutoCallin({
          details: this.details,
          userId: this.user.id,
          positionId: this.position.id,
          allowNewGrads: this.allowNewGrads,
          requireAdvancedAssistants: this.requireAdvancedAssistants,
          locationId: this.location.id,
          shiftStart: this.shiftStart,
          shiftEnd: this.shiftEnd,
          straightBonusAmount: this.straightBonusAmount,
          callInBonusAmount: this.callInBonusAmount,
          travelBonusAmount: this.travelBonusAmount,
          hasCoverBonus: this.hasCoverBonus,
          assignedUserId: assignedUserId,
          repeats: this.repeats,
          repeatFrequency: this.repeatFrequency,
          repeatDuration: this.repeatDuration,
          requiredSkillIds: this.skillsSelected
        })
        .then(r => {
          const message = r.data.message;
          if (message === "") {
            this.createShift();
          } else {
            this.autoCallinMessage = message;
            this.autoCallinWarning = true;
          }
        });
      return;
    },

    createShift() {
      var assignedUserId = null;
      if (this.assignedEmployee) {
        var assignedUserId = this.assignedEmployee.id;
      }

      // coordinated coverage
      if (this.user.role == "client" && this.assignedEmployee) {
        this.$store
          .dispatch("coordinatedCoverageRequests/create", {
            shift_create_params: {
              approved: true,
              worksite_id: this.location.id,
              employee_position_id: this.position.id,
              start_datetime: this.shiftStart,
              end_datetime: this.shiftEnd,
              details: this.details,
              straight_bonus_amount: this.straightBonusAmount,
              call_in_bonus_amount: this.callInBonusAmount,
              travel_bonus_amount: this.travelBonusAmount,
              has_cover_bonus: this.hasCoverBonus,
              required_skill_ids: this.skillsSelected,
              new_grads_allowed: this.allowNewGrads,
              advanced_assistants_required: this.requireAdvancedAssistants,
              sunset_after: this.sunsetDate
            },
            requested_by_user_id: this.user.id,
            employee_requested_user_id: this.assignedEmployee.id,
            must_fulfill: this.mustFulfill
          })
          .then(response => {
            this.$router.push({
              name: "coordinatedCoverageRequestShow",
              params: {
                coordinatedCoverageRequestId:
                  response.coordinatedCoverageRequest.id
              }
            });
          })
          .catch(err => {});
      } else {
        // standard shift
        this.$store
          .dispatch("serviceRequest/create", {
            details: this.details,
            userId: this.user.id,
            positionId: this.position.id,
            allowNewGrads: this.allowNewGrads,
            requireAdvancedAssistants: this.requireAdvancedAssistants,
            locationId: this.location.id,
            shiftStart: this.shiftStart,
            shiftEnd: this.shiftEnd,
            straightBonusAmount: this.straightBonusAmount,
            callInBonusAmount: this.callInBonusAmount,
            travelBonusAmount: this.travelBonusAmount,
            hasCoverBonus: this.hasCoverBonus,
            assignedUserId: assignedUserId,
            repeats: this.repeats,
            repeatFrequency: this.repeatFrequency,
            repeatDuration: this.repeatDuration,
            requiredSkillIds: this.skillsSelected,
            sunset_after: this.sunsetDate
          })
          .then(response => {
            let shift = response;
            this.$router.push({
              name: "shiftDetail",
              params: { shiftId: shift.id }
            });
          })
          .catch(err => {});
      }
    }
  },
  data: function() {
    return {
      autoCallinWarning: false,
      autoCallinMessage: "",
      bulkCreate: false,
      eligibleEmployees: [],
      valid: false,
      limitedToSkills: false,
      allowNewGrads: false,
      requireAdvancedAssistants: false,
      showAdvanced: false,
      details: null,
      position: null,
      location: null,
      shiftStart: null,
      shiftEnd: null,
      skillsPending: false,
      skillsSelected: [],
      possibleSkills: [],
      details: null,
      straightBonusAmount: null,
      callInBonusAmount: null,
      travelBonusAmount: null,
      hasCoverBonus: false,
      coordinatedCoverage: false,
      mustFulfill: false,
      repeats: false,
      assignedEmployee: null,
      repetitionOptions: [],
      repeatFrequency: null,
      repeatDuration: null,
      repeatDurationPicker: false,
      sunsetOn: false,
      sunsetAfterDays: null,
      sunsetAfterDayRange: range(3, 31),
      sunsetDate: null,
      canSetSunsetDays: true,
      locationRules: [v => !!v || "Location is required"],
      positionRules: [v => !!v || "Position is required"],
      // employeeRules: [
      //   function () {
      //     if (this.coordinatedCoverage) {
      //       v => !!v || 'Employee is required'
      //       console.log('testing 123 it works')
      //     }
      //   }
      // ],
      startTimeMessages: [],
      endTimeMessages: []
    };
  }
};
</script>

<style>
.straight-bonus-field .v-messages__message {
  color: red !important;
}
</style>

<style scoped>
.v-divider {
  border-color: rgb(145, 145, 145);
}
</style>
